import API from '@services/API';
import { decrementCommunicationPreferenceConfirmationCount, incrementCommunicationPreferenceConfirmationCount, setError, startSpinner, stopSpinner, updateCommunicationPreferenceConfirmationCount } from '../../common/actionCreators';
import { configs } from '@constants/configs/configs';

import {
    COMMUNICATION_PREFERENCES_ACTION_TYPES,
    communicationPreferencesSagaActionTypes,
    languagesSagaActionTypes
} from '@constants/actionTypes/dashboardActionTypes';

// Types

import { CommunicationPreferences, Languages, ReduxAction } from '@components/ssap/SsapTypes';
import { communicationPreferencesReviewTypes } from '@constants/individualPortal/preferences/preferenceTypes';

interface CommunicationPreferencesResponse {
    message: null | string,
    payload: CommunicationPreferences,
    success: boolean
}

interface LanguagesResponse {
    message: null | string,
    payload: Languages,
    success: boolean
}


const actions: { [ key: string ]: Function } = { setError, startSpinner, stopSpinner };
const URL = configs.endpoints.COMMUNICATION_PREFERENCES;
const URL_TELEX_ENV = configs.endpoints.TELEX_ENVIRONMENT;
const URL_PARTIAL_UPDATE = configs.endpoints.PARTIAL_UPDATE;
const URL_OTP_VERIFY = configs.endpoints.OTP_VERIFY;
const URL_OTP_SEND = configs.endpoints.OTP_SEND;
const URL_CHECK_EMAIL = configs.endpoints.CHECK_EMAIL_AVAILABILITY;
const URL_UPDATE_PREFERENCES_CONFIRMATION = configs.endpoints.UPDATE_PREFERENCES_CONFIRMATION;
const URL_LANGUAGES = configs.endpoints.LANGUAGES_SPOKEN_WRITTEN;

// Async ActionCreators

export const fetchExchangeDetails = () => async (dispatch, getState): Promise<any> => {

    const response: CommunicationPreferencesResponse = await API.getData(URL_TELEX_ENV, dispatch, actions, true, getState());

    if (response) {
        return response;
    }
};

export const fetchLanguages = () => async (dispatch, getState): Promise<LanguagesResponse | null> => {
    const response = await API.getData(URL_LANGUAGES, dispatch, actions, true, getState());
    return response || null;
};

export const fetchPreferences = () => async (dispatch, getState): Promise<any> => {

    const response: CommunicationPreferencesResponse = await API.getData(URL, dispatch, actions, true, getState());

    if (response) {
        return response;
    }
};

export const savePreferences = (...args) => async (dispatch, getState): Promise<any> => {

    const [ body ] = args;
    let response: CommunicationPreferencesResponse;

    response = await API.postData([URL, body], dispatch, actions);

    if (response) {
        return response;
    }
};

export const partialUpdatePreferences = (...args) => async (dispatch, getState): Promise<any> => {
    const [body] = args;

    let response: CommunicationPreferencesResponse;

    if (configs.ENV.DEVELOPMENT) {
        response = await API.getData(URL_PARTIAL_UPDATE, dispatch, actions, true, getState());
    } else {
        response = await API.postData([URL_PARTIAL_UPDATE, body], dispatch, actions);
    }

    if (response) {
        return response;
    }
};

export const otpVerify = (...args) => async (dispatch, getState): Promise<any> => {
    const [body] = args;

    let response: CommunicationPreferencesResponse;

    response = await API.postData([URL_OTP_VERIFY, body], dispatch, actions);

    if (response) {
        return response;
    }
};

export const checkEmailAvailability = (email: string) => async (dispatch): Promise<boolean> => {
    const formBody = `email=${encodeURIComponent(email)}`;
    const response = await API.postData(
        [URL_CHECK_EMAIL, formBody, 'POST', true, undefined, 'application/x-www-form-urlencoded', true, false, true],
        dispatch,
        actions,
    );
    return response;
};

export const otpSend = (...args) => async (dispatch, getState): Promise<any> => {
    const [body] = args;
    let response: CommunicationPreferencesResponse;

    response = await API.postData([URL_OTP_SEND, body], dispatch, actions);

    if (response) {
        return response;
    }
};

// Sync action creators

export const setCommunicationPreferences = (preferences: CommunicationPreferences): ReduxAction => ({
    type: COMMUNICATION_PREFERENCES_ACTION_TYPES.SET_COMMUNICATION_PREFERENCES,
    preferences
});

export const setLangauges = (languages): ReduxAction => ({
    type: COMMUNICATION_PREFERENCES_ACTION_TYPES.SET_LANGUAGES,
    languages
});

// communicationPreferencesSagaActions

export const fetchCommunicationPreferences = (callbacks = null, shouldForceUpdate = false, skipFailureRedirect = false) => ({
    type: communicationPreferencesSagaActionTypes.FETCH_COMMUNICATION_PREFERENCES,
    callbacks,
    shouldForceUpdate,
    skipFailureRedirect
});

export const startFetchingPreferences = () => ({
    type: communicationPreferencesSagaActionTypes.FETCH_COMMUNICATION_PREFERENCES_START
});

export const fetchCommunicationPreferencesFailure = (skipFailureRedirect = false): ReduxAction => ({
    type: communicationPreferencesSagaActionTypes.FETCH_COMMUNICATION_PREFERENCES_FAILURE,
    skipFailureRedirect
});

export const fetchCommunicationPreferencesSuccess = (): ReduxAction => ({
    type: communicationPreferencesSagaActionTypes.FETCH_COMMUNICATION_PREFERENCES_SUCCESS
});

export const fetchPortalLanguages = (skipFailureRedirect = false) => ({
    type: languagesSagaActionTypes.FETCH_LANGUAGES,
    skipFailureRedirect
});

export const startFetchingLanguages = (): ReduxAction => ({
    type: languagesSagaActionTypes.FETCH_LANGUAGES_START
});

export const fetchLanguagesSuccess = (): ReduxAction => ({
    type: languagesSagaActionTypes.FETCH_LANGUAGES_SUCCESS
});

export const fetchLanguagesFailure = (skipFailureRedirect = false): ReduxAction => ({
    type: languagesSagaActionTypes.FETCH_LANGUAGES_FAILURE,
    skipFailureRedirect
});

export const updateCommunicationPreferenceConfirmation = (body) => async (dispatch): Promise<any> => {
    dispatch(decrementCommunicationPreferenceConfirmationCount());
    const response: CommunicationPreferencesResponse = await API.postData([URL_UPDATE_PREFERENCES_CONFIRMATION, body, 'PUT', false], dispatch, actions);
    if (!response?.success) dispatch(incrementCommunicationPreferenceConfirmationCount());
    return response;
};

export const updateCommunicationPreferenceCount = (data:CommunicationPreferencesResponse) => (dispatch) => {
    const communicationPreferenceReviewCount = Object.values(communicationPreferencesReviewTypes).reduce((acc, item) => {
        if (data?.[item]) acc += 1;
        return acc;
    }, 0);
    dispatch(updateCommunicationPreferenceConfirmationCount(communicationPreferenceReviewCount));
};